import {
  AUDIENCE_LANGUAGE,
  type AudienceContent,
  type DgContent,
  type DraftArtifactKind,
  type RqsContent,
  audienceContentSchema,
  countryNamesInReadingOrder,
  dgContentSchema,
  displayMinutes,
  guideMinutes,
  rqsContentSchema,
} from '@sigora/domain';
import { Fragment, useState } from 'react';

import type { AudienceCatalog } from '../api/client.ts';
import { type AudienceLabel, labelAudienceFilter } from './audience-labels.ts';

/** Plain-language question types, never the underlying platform's names for them. */
const QUESTION_TYPE_LABELS: Record<string, string> = {
  open_response: 'Open-ended',
  single_choice: 'Multiple choice — one answer',
  multiple_choice: 'Multiple choice — several answers',
};

export interface ArtifactBodyProps {
  kind: DraftArtifactKind;
  content: unknown;
  /** The read failed, as opposed to not having finished — a different thing to say. */
  contentUnreadable: boolean;
  /**
   * The version `content` came from, which a full-document save has to quote back. Undefined when
   * that cannot be established — in which case editing is closed rather than guessed at, because
   * a quoted version the document did not come from is exactly the silent revert the precondition
   * exists to refuse.
   */
  version: number | undefined;
  /**
   * The panel's demographics, for turning the audience's stored ids into words. Null while the
   * one fetch the shell makes is still out, or where it failed — the card renders the ids in that
   * case rather than waiting, because a filter list is readable unlabelled and a blank one is not.
   */
  audienceCatalog: AudienceCatalog | null;
  /** Editing closes once the study is locked, and while a write is in flight. */
  disabled: boolean;
  /**
   * `baseVersion` is the version the editor was opened on, not the version showing when Save was
   * pressed. The two differ whenever a turn lands under an open editor, and sending the second
   * would assert authorship of a document the user never saw — the precise overwrite
   * `expectedVersion` exists to refuse.
   */
  onSaveDocument: (content: unknown, baseVersion: number) => void;
  /** `baseVersion` is the version the wording editor was opened on, for the same reason. */
  onRewordQuestion: (questionId: string, text: string, baseVersion: number) => void;
}

/**
 * The document itself. Stored content is parsed before it is rendered rather than trusted: it can
 * have been written by an older shape of the schema, and a card that renders blank is a worse
 * answer than one that says the content could not be read.
 */
export const ArtifactBody = ({
  kind,
  content,
  contentUnreadable,
  version,
  audienceCatalog,
  disabled,
  onSaveDocument,
  onRewordQuestion,
}: ArtifactBodyProps) => {
  if (contentUnreadable) {
    // The artifact exists and its chrome above is accurate; only the document failed to arrive.
    // Saying "loading" forever would be a lie, and saying "not written yet" a worse one.
    return (
      <p data-test-id="artifact-content-failed">
        This could not be loaded just now. Everything above is current; reload to try again.
      </p>
    );
  }

  if (content === undefined) {
    return <p data-test-id="artifact-loading">Loading…</p>;
  }

  // Editing needs a version to quote; without one the document is readable but not editable.
  const editable = version !== undefined && !disabled;

  if (kind === 'rqs') {
    const parsed = rqsContentSchema.safeParse(content);

    return parsed.success ? (
      <RqsBody
        content={parsed.data}
        version={version ?? 0}
        disabled={!editable}
        onSave={onSaveDocument}
      />
    ) : (
      <UnreadableContent />
    );
  }

  if (kind === 'dg') {
    const parsed = dgContentSchema.safeParse(content);

    return parsed.success ? (
      <DgBody
        content={parsed.data}
        version={version ?? 0}
        disabled={!editable}
        onReword={onRewordQuestion}
      />
    ) : (
      <UnreadableContent />
    );
  }

  const parsed = audienceContentSchema.safeParse(content);

  return parsed.success ? (
    <AudienceBody content={parsed.data} catalog={audienceCatalog} />
  ) : (
    <UnreadableContent />
  );
};

/**
 * What an open editor is holding: the user's working copy, and the version it was seeded from.
 * The pair has to be one piece of state — a draft whose base version can be updated
 * independently is exactly the bug this shape exists to make unrepresentable.
 */
interface EditSession<Content> {
  draft: Content;
  baseVersion: number;
}

const UnreadableContent = () => (
  <p data-test-id="artifact-unreadable">
    This version cannot be shown here. Ask the agent to write it out in the conversation.
  </p>
);

/**
 * Research questions are the user's to write, so the whole document is theirs to submit — which
 * is why the save carries the version this list was built from. Rewording one question means
 * sending a document that differs from the stored one in one field, and if the stored one has
 * moved since, that document would revert whatever moved.
 */
const RqsBody = ({
  content,
  version,
  disabled,
  onSave,
}: {
  content: RqsContent;
  version: number;
  disabled: boolean;
  onSave: (content: unknown, baseVersion: number) => void;
}) => {
  const [session, setSession] = useState<EditSession<RqsContent> | null>(null);

  const items = session === null ? content.items : session.draft.items;

  return (
    <div data-test-id="rqs-body">
      <ol className="artifact-list">
        {items.map((item, index) => (
          <li key={item.id}>
            {session !== null ? (
              <>
                <label className="visually-hidden" htmlFor={`rq-${item.id}`}>
                  Research question {index + 1}
                </label>
                <textarea
                  id={`rq-${item.id}`}
                  data-test-id={`rq-text-${item.id}`}
                  rows={2}
                  value={item.text}
                  onChange={(event) =>
                    setSession((previous) =>
                      previous === null
                        ? previous
                        : {
                            ...previous,
                            draft: {
                              ...previous.draft,
                              items: previous.draft.items.map((candidate) =>
                                candidate.id === item.id
                                  ? { ...candidate, text: event.target.value }
                                  : candidate,
                              ),
                            },
                          },
                    )
                  }
                />
              </>
            ) : (
              <p data-test-id={`rq-${item.id}`}>{item.text}</p>
            )}
            {item.decision !== '' && <p className="artifact-meta">Informs: {item.decision}</p>}
          </li>
        ))}
      </ol>

      {session !== null ? (
        <div className="artifact-actions">
          <button
            type="button"
            data-test-id="rqs-save"
            disabled={disabled}
            onClick={() => {
              onSave(session.draft, session.baseVersion);
              setSession(null);
            }}
          >
            Save
          </button>
          <button type="button" data-test-id="rqs-cancel" onClick={() => setSession(null)}>
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-test-id="rqs-edit"
          disabled={disabled}
          onClick={() => setSession({ draft: content, baseVersion: version })}
        >
          Edit
        </button>
      )}
    </div>
  );
};

/**
 * The guide is the agent's structure and the user's words. Only the question text is editable,
 * and each one saves on its own through the narrow path, quoting the version it was opened on — so
 * a reworded question does not carry the rest of the document with it, and one made over a guide
 * that has moved since is refused rather than applied to a guide the user was not shown.
 */
const DgBody = ({
  content,
  version,
  disabled,
  onReword,
}: {
  content: DgContent;
  version: number;
  disabled: boolean;
  onReword: (questionId: string, text: string, baseVersion: number) => void;
}) => {
  const [editing, setEditing] = useState<{ id: string; baseVersion: number } | null>(null);
  const [draft, setDraft] = useState('');
  const editingId = editing?.id ?? null;

  return (
    <div data-test-id="dg-body">
      <p className="artifact-meta" data-test-id="dg-duration">
        About {displayMinutes(guideMinutes(content))} minutes per person
      </p>

      {content.sections.map((section) => (
        <section key={section.id} className="artifact-section">
          <h4>{section.title}</h4>
          <ol className="artifact-list">
            {section.questions.map((question) => (
              <li key={question.id}>
                {editingId === question.id ? (
                  <>
                    <label className="visually-hidden" htmlFor={`dg-${question.id}`}>
                      Question wording
                    </label>
                    <textarea
                      id={`dg-${question.id}`}
                      data-test-id={`dg-text-${question.id}`}
                      rows={2}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                    />
                    <div className="artifact-actions">
                      <button
                        type="button"
                        data-test-id={`dg-save-${question.id}`}
                        disabled={disabled}
                        onClick={() => {
                          setEditing(null);
                          onReword(question.id, draft, editing?.baseVersion ?? version);
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        data-test-id={`dg-cancel-${question.id}`}
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p data-test-id={`dg-${question.id}`}>{question.text}</p>
                    <p className="artifact-meta">
                      {QUESTION_TYPE_LABELS[question.type] ?? question.type}
                    </p>
                    {question.options !== undefined && (
                      <ul className="artifact-options">
                        {question.options.map((option) => (
                          <li key={option.id}>{option.text}</li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      data-test-id={`dg-edit-${question.id}`}
                      disabled={disabled}
                      onClick={() => {
                        setDraft(question.text);
                        setEditing({ id: question.id, baseVersion: version });
                      }}
                    >
                      Edit
                    </button>
                  </>
                )}
              </li>
            ))}
          </ol>
        </section>
      ))}

      <p className="artifact-meta" data-test-id="dg-structure-note">
        Adding, removing or reordering questions is the agent&rsquo;s job — ask it in the
        conversation.
      </p>
    </div>
  );
};

/**
 * One demographic or one choice, in the panel's words where the catalog has them.
 *
 * A value the catalog does not have is muted rather than hidden or flagged: it is still the truth
 * about what the study targets, and the mute is the whole of what the card is entitled to say
 * about it — that the panel does not list it, not that it is wrong. A value shown because there
 * was no catalog to check it against is not muted; nothing disowned it.
 */
const AudienceValue = ({ label }: { label: AudienceLabel }) =>
  label.unrecognised ? (
    <span className="audience-unlabelled" data-test-id="audience-unlabelled">
      {label.text}
    </span>
  ) : (
    <span>{label.text}</span>
  );

/**
 * The audience, read-only: who, where, the language, the filter list as the honest contract, the
 * screening questions with the options that end the survey, and the Limitations. Capture-only
 * filters are labelled as such: they are collected for analysis rather than used to narrow who is
 * recruited, and a user reading them as targeting would think their audience was narrower than it
 * is. Nothing here is edited: the count, countries, filters and screeners change through Sigora.
 */
const AudienceBody = ({
  content,
  catalog,
}: {
  content: AudienceContent;
  catalog: AudienceCatalog | null;
}) => (
  <div data-test-id="audience-body">
    <p data-test-id="audience-description">
      <strong>Who:</strong> {content.description}
    </p>
    <p className="artifact-meta" data-test-id="audience-completes">
      {content.target_completes} people
    </p>
    <p className="artifact-meta" data-test-id="audience-countries">
      <strong>Where:</strong> {countryNamesInReadingOrder(content.countries).join(' and ')}
    </p>
    <p className="artifact-meta" data-test-id="audience-language">
      <strong>Language:</strong> {AUDIENCE_LANGUAGE}
    </p>

    <ul className="artifact-list" data-test-id="audience-filters">
      {content.filters.map((filter) => {
        const labelled = labelAudienceFilter(filter, catalog);

        return (
          <li key={filter.id}>
            <span>
              <AudienceValue label={labelled.dimension} />
              {labelled.range ? ' ' : ': '}
              {labelled.options.map((option, index) => (
                // Position, because the same option can legitimately appear twice in a
                // document and neither the id nor the label is unique in that case.
                <Fragment key={index}>
                  {index > 0 && ', '}
                  <AudienceValue label={option} />
                </Fragment>
              ))}
            </span>
            {filter.mode === 'capture_only' && (
              <span className="artifact-meta" data-test-id={`audience-capture-${filter.id}`}>
                Collected for analysis, not used to choose who takes part
              </span>
            )}
          </li>
        );
      })}
    </ul>

    {content.screeners.length > 0 && (
      <ul className="artifact-list" data-test-id="audience-screeners">
        {content.screeners.map((screener) => (
          <li key={screener.id} data-test-id="audience-screener">
            <span>{screener.text}</span>
            <ul>
              {screener.options.map((option) => (
                <li key={option.id}>
                  {option.text}
                  {option.terminates && <span className="artifact-meta"> Ends the survey</span>}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    )}

    {(content.limitations.targeting_gaps.length > 0 ||
      content.limitations.pipeline_constraints.length > 0) && (
      <ul className="artifact-caveats" data-test-id="audience-limitations">
        {content.limitations.targeting_gaps.map((gap) => (
          <li key={gap.id}>{gap.text}</li>
        ))}
        {content.limitations.pipeline_constraints.map((constraint) => (
          <li key={constraint.key}>{constraint.text}</li>
        ))}
      </ul>
    )}
  </div>
);
